<template>
  <div class="min-h-screen bg-gray-50">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-2xl mx-auto">
        <a :href="backUrl" class="text-emerald-200 hover:text-white text-sm">&larr; Speak Saigon</a>
        <h1 class="text-2xl font-bold mt-1">Vietnamese Tone Trainer</h1>
        <p class="text-emerald-100 text-sm mt-1">Hear it. Say it. See your pitch curve against the {{ accent }}-accent reference — free, no signup.</p>
      </div>
    </header>

    <main class="max-w-2xl mx-auto px-4 py-6 space-y-4">
      <div v-if="loading" class="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        Loading tones...
      </div>

      <template v-else>
        <!-- Up-front warning for Instagram/Facebook webviews. Shown before the
             first tap rather than after a failed recording: the whole page is
             worthless without a mic, so the escape hatch has to come first. -->
        <div v-if="inAppBrowser" class="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <p class="text-sm font-semibold text-amber-900">Recording won't work in this in-app browser</p>
          <p class="mt-0.5 text-sm text-amber-800">{{ inAppEscapeHint }} You can still listen to every tone here.</p>
        </div>

        <!-- Dialect toggle: only rendered when the backend advertises more than
             one dialect (i.e. the Northern audio has been generated). Saigon
             stays the default — this is a Southern-focused app. -->
        <div v-if="dialects.length > 1" class="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          <button
            v-for="d in dialects"
            :key="d.id"
            @click="selectDialect(d.id)"
            :class="[
              'px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
              dialect === d.id ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:text-gray-900'
            ]"
          >
            {{ d.label }}
          </button>
        </div>

        <!-- Tone picker -->
        <div class="flex flex-wrap gap-2">
          <button
            v-for="item in items"
            :key="item.id"
            @click="selectItem(item)"
            :class="[
              'px-3.5 py-2 rounded-full text-sm font-semibold border-2 transition-colors',
              current?.id === item.id
                ? 'border-emerald-600 bg-emerald-600 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300'
            ]"
          >
            {{ item.tone }} <span :class="current?.id === item.id ? 'text-emerald-100' : 'text-gray-400'">{{ item.mark }}</span>
          </button>
        </div>

        <!-- Set progress, visible from the first paint. This used to live inside
             the post-record card, which meant nobody knew finishing the set was
             a goal until they'd already recorded — and nobody ever finished. -->
        <div v-if="items.length" class="flex items-center gap-2">
          <span
            v-for="item in items"
            :key="item.id"
            :title="item.tone"
            :class="[
              'w-2.5 h-2.5 rounded-full transition-colors',
              recordedIds.includes(item.id) ? 'bg-emerald-500' : 'bg-gray-300'
            ]"
          ></span>
          <span class="ml-1 text-sm text-gray-500">
            <template v-if="allTonesTried">All {{ items.length }} tones tried &mdash; nice.</template>
            <template v-else>{{ recordedIds.length }} of {{ items.length }} tones tried</template>
          </span>
        </div>

        <!-- Current tone card -->
        <div v-if="current" ref="toneCard" class="bg-white rounded-xl border border-gray-200 p-6">
          <p class="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{{ current.tone }} — {{ current.shape }}</p>
          <p class="mt-1 text-sm text-gray-500">{{ current.how }}</p>

          <!-- Syllable / word toggle -->
          <div class="mt-4 flex gap-2">
            <button
              v-for="kind in ['syllable', 'word']"
              :key="kind"
              @click="selectKind(kind)"
              :class="[
                'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                currentKind === kind ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
              ]"
            >
              {{ kind === 'syllable' ? 'Practice syllable' : 'Real word' }}
            </button>
          </div>

          <!-- Wraps because "Real word" targets are full words, not the short
               syllables this row was laid out for — at 5xl a longer one plus the
               play button plus the gloss overruns a phone. shrink-0 keeps the
               button circular instead of being squashed into an ellipse. -->
          <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span class="text-4xl sm:text-5xl font-extrabold text-gray-900 break-words">{{ target.text }}</span>
            <button
              @click="playReference"
              class="shrink-0 grid place-items-center w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xl shadow-sm transition-colors"
              aria-label="Hear the reference pronunciation"
            >
              🔊
            </button>
            <span class="text-gray-500 text-sm">"{{ target.meaning }}"</span>
          </div>

          <!-- Recorder -->
          <div class="mt-6 flex flex-col items-center gap-2">
            <button
              @click="toggleRecording"
              :disabled="recState === 'processing' || retrySeconds > 0"
              :class="[
                'w-16 h-16 rounded-full flex items-center justify-center text-2xl transition-all',
                recState === 'recording'
                  ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-200'
                  : recState === 'processing' || retrySeconds > 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              ]"
            >
              {{ recState === 'recording' ? '⏹' : recState === 'processing' ? '⏳' : '🎤' }}
            </button>
            <p class="text-xs text-gray-500">
              {{ recState === 'recording' ? 'Recording... tap to stop' : recState === 'processing' ? 'Analyzing your pitch...' : 'Listen first, then tap to record yourself' }}
            </p>
            <!-- Permission priming. 68 of 75 mic_denied errors in the 30 days to
                 2026-07-20 came from ordinary mobile/desktop browsers, not
                 webviews — i.e. people meeting a bare OS permission prompt with
                 no idea why. Saying what we ask for and what happens to the
                 audio, before the prompt, is the whole fix. The "never stored"
                 claim is literally true: pitch_service.py analyses the upload in
                 a NamedTemporaryFile(delete=True) and persists nothing. -->
            <p v-if="!micGranted && !errorMessage" class="text-xs text-gray-400 text-center max-w-xs">
              🔒 Your browser will ask for microphone access. Recordings are analysed for pitch and never stored.
            </p>
            <p v-if="errorMessage" class="text-sm text-red-500 text-center">{{ errorMessage }}</p>
          </div>

          <!-- Chart: the reference curve shows immediately (the target shape);
               the learner's curve joins it after they record. -->
          <div v-if="referenceContour?.length > 1" class="mt-5">
            <ToneContour
              :reference="referenceContour"
              :user="userContour?.length > 1 ? userContour : null"
              :reference-label="`${accent} reference`"
            />
            <p class="text-center mt-2 text-sm text-gray-500">
              {{ userContour?.length > 1 ? 'Match the shape — then tap 🎤 and try again.' : 'This is the shape to match — tap 🎤 and copy it.' }}
            </p>
          </div>
        </div>

        <!-- Locked practice affordance: the free tool only cycles the preset
             tones, so this surfaces the members capability and routes to signup.
             It must advertise the FREE tier's capability (300+ course words and
             their example sentences), not typing arbitrary text — custom text is
             Pro-only (TonePracticeController#custom_reference), and the earlier
             "type any word … unlock free" wording walked new accounts straight
             into a paywall. The whole card is the click target. -->
        <a
          v-if="current"
          :href="tonePracticeSignupUrl"
          @click="track('tone_trainer_custom_locked_clicked', { placement: 'custom_input' })"
          class="block bg-white rounded-xl border-2 border-dashed border-emerald-300 p-4 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
        >
          <!-- min-w-0 is what makes `truncate` work: a flex child defaults to
               min-width:auto and refuses to shrink below its content, so without
               it the label pushed "Unlock free →" off the right edge on a phone
               instead of ellipsing. -->
          <div class="flex items-center gap-3">
            <div class="flex-1 min-w-0 flex items-center gap-2 text-gray-400">
              <span class="text-lg shrink-0">🔒</span>
              <span class="text-sm truncate">Practise these tones on 300+ real words…</span>
            </div>
            <span class="shrink-0 whitespace-nowrap font-semibold text-emerald-700 text-sm">Unlock free →</span>
          </div>
          <p class="mt-2 text-xs text-gray-500">A free account scores 15 recordings a day on every course word and example sentence — not just the tones above.</p>
        </a>

        <!-- Conversion moment. Appears once the visitor has recorded — in this
             session or an earlier one, since progress is persisted, so returners
             land straight back on their next step rather than a cold page. At
             their peak intent: feedback, one clear next action, and an email
             catch for everyone who won't click through. -->
        <div v-if="hasRecorded" class="rounded-xl border-2 border-emerald-200 bg-white overflow-hidden">
          <!-- Scoring feedback (this attempt only — blank for a returner who
               hasn't recorded yet this session). -->
          <div class="px-6 pt-6">
            <p
              v-if="feedback"
              class="text-xs font-semibold uppercase tracking-wide"
              :class="{
                'text-emerald-600': feedback.tier === 'great',
                'text-amber-600': feedback.tier === 'close' || feedback.tier === 'keep',
                'text-gray-500': feedback.tier === 'ok',
              }"
            >{{ feedback.headline }}</p>
            <p v-if="feedback" class="mt-1 text-gray-700">{{ feedback.text }}</p>
          </div>

          <!-- Hero, in two states. Mid-set it's the next untried tone: the set
               is what earns the pitch below, so nothing should pull people out
               of it. Once every tone is done the visitor has exhausted the free
               tool, and the hero becomes the account — "this exact loop, on 300+
               real words". -->
          <div v-if="allTonesTried" class="px-6 py-5">
            <p class="font-bold text-gray-900">All {{ items.length }} tones down. Now point it at any word you want.</p>
            <p class="mt-0.5 text-sm text-gray-500">
              A free account scores <span class="font-semibold text-gray-700">15 recordings a day</span> on any of 300+ course words and their example sentences — same pitch curve, real vocabulary. No card.
            </p>
            <a
              :href="tonePracticeSignupUrl"
              @click="track('tone_trainer_signup_clicked', { placement: 'all_tones', dialect: dialect })"
              class="mt-3 inline-flex items-center justify-center font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-6 py-3 rounded-xl shadow-sm transition-colors"
            >
              Unlock any word — free →
            </a>
            <p class="mt-2 text-sm text-gray-500">
              Not ready? <a :href="demoUrl" @click="track('tone_trainer_cta_clicked', {})" class="text-emerald-700 underline font-medium">Try a 60-second lesson first</a> — no signup.
            </p>
          </div>
          <!-- Mid-set: two equal offers. "Next tone" drives set completion (which
               is what earns the signup pitch above), but the demo link is measurably
               the most-clicked CTA on this page — 23 clicks/30d against 0 for the
               completion CTA — so it keeps equal billing rather than being demoted
               to body text. Same emerald/white button pair the landing hero uses. -->
          <div v-else class="px-6 py-5">
            <p class="font-bold text-gray-900">
              {{ items.length - recordedIds.length }} {{ items.length - recordedIds.length === 1 ? 'tone' : 'tones' }} to go — next up, {{ nextUntried?.tone }}.
            </p>
            <p class="mt-0.5 text-sm text-gray-500">Try all {{ items.length }} and you'll have covered every tone in the language.</p>
            <div class="mt-3 flex flex-col sm:flex-row gap-2">
              <button
                v-if="nextUntried"
                @click="goToNextUntried"
                class="inline-flex items-center justify-center font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-6 py-3 rounded-xl shadow-sm transition-colors"
              >
                Next tone: {{ nextUntried.tone }} {{ nextUntried.mark }} →
              </button>
              <a
                :href="demoUrl"
                @click="track('tone_trainer_cta_clicked', {})"
                class="inline-flex items-center justify-center font-semibold bg-white border border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-800 px-6 py-3 rounded-xl transition-colors"
              >
                Try a free lesson →
              </a>
            </div>
          </div>

          <!-- Removed 2026-07-20: the email catch that used to sit here captured 0
               addresses from 185 recorders over 30 days while occupying the space
               directly under the CTA we want clicked. The server side (Lead,
               LeadMailer, LeadNudgeJob, the tone_trainer#lead route) is untouched
               and can still be reached — only the dead UI is gone. -->
        </div>

        <!-- Pre-record CTA: shown until the visitor has tried recording. -->
        <div v-else class="rounded-xl bg-emerald-600 px-6 py-8 text-center">
          <p class="text-lg font-bold text-white">Tones clicking? Learn the words to go with them.</p>
          <p class="mt-1 text-emerald-50 text-sm">Full lessons with Saigon-accent audio — or the Hanoi accent, now in beta — plus games and spaced review.</p>
          <a
            :href="demoUrl"
            @click="track('tone_trainer_cta_clicked', {})"
            class="mt-4 inline-flex items-center justify-center font-semibold bg-white text-emerald-700 hover:bg-emerald-50 px-6 py-3 rounded-xl shadow-sm transition-colors"
          >
            Try a free lesson — no signup
          </a>
          <p class="mt-3 text-sm text-emerald-100">
            Or <a href="/vietnamese" @click="track('tone_trainer_phrasebook_clicked', {})" class="underline font-medium hover:text-white">browse the free phrasebook</a> — every word with Saigon audio.
          </p>
        </div>
      </template>

      <!-- Keeps the fixed offer bar from covering the last block on the page.
           Taller below sm because the bar wraps to two rows there. -->
      <div v-if="showStickyOffer" class="h-28 sm:h-16" aria-hidden="true"></div>
    </main>

    <!-- Persistent offer bar. One line, pinned to the viewport, so the ask is
         reachable without scrolling on a tool people use and leave. Sits below
         the modal in z-order and hides while it's open. -->
    <Transition
      enter-active-class="transition duration-200 ease-out" enter-from-class="opacity-0 translate-y-full"
      leave-active-class="transition duration-150 ease-in" leave-to-class="opacity-0 translate-y-full"
    >
      <div v-if="showStickyOffer" class="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
        <!-- Wraps to two rows on a phone rather than truncating. The label was
             ellipsing to "Practise these tones on 300+ re…", which is worse than
             no bar at all: it occupies the screen while hiding the actual offer.
             basis-full gives the text its own row below the sm breakpoint; the
             button and dismiss share the second. -->
        <div class="relative max-w-2xl mx-auto px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <a
            :href="tonePracticeSignupUrl"
            @click="track('tone_trainer_signup_clicked', { placement: 'sticky_bar', dialect: dialect })"
            class="basis-full sm:basis-0 sm:flex-1 min-w-0 flex items-center gap-2 pr-9 sm:pr-0"
          >
            <span class="text-base shrink-0">🔒</span>
            <span class="text-sm text-gray-600 leading-snug">
              Practise these tones on <span class="font-semibold text-gray-900">300+ real words</span>
            </span>
          </a>
          <a
            :href="tonePracticeSignupUrl"
            @click="track('tone_trainer_signup_clicked', { placement: 'sticky_bar', dialect: dialect })"
            class="flex-1 sm:flex-none inline-flex items-center justify-center whitespace-nowrap font-semibold bg-emerald-600 text-white hover:bg-emerald-700 text-sm px-4 py-2.5 rounded-lg shadow-sm transition-colors"
          >
            Unlock free →
          </a>
          <!-- Corner-pinned on mobile so the CTA gets the full width of its row;
               back in the flow at sm, where everything fits on one line. -->
          <button
            @click="dismissStickyOffer"
            aria-label="Dismiss"
            class="absolute top-1.5 right-2 sm:static shrink-0 grid place-items-center w-9 h-9 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
    </Transition>

    <!-- Completion offer. Same words as the inline card below the fold, delivered
         at the moment it's earned instead of waiting to be scrolled to. Stays
         dismissible and remembers the dismissal — someone who says no should be
         able to keep using the tool without being asked again. -->
    <Transition
      enter-active-class="transition duration-200 ease-out" enter-from-class="opacity-0"
      leave-active-class="transition duration-150 ease-in" leave-to-class="opacity-0"
    >
      <div
        v-if="showOfferModal"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-900/60 p-0 sm:p-4"
        @click.self="dismissOffer('backdrop')"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="tone-offer-title"
          class="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl p-6 sm:p-7"
        >
          <button
            @click="dismissOffer('close_button')"
            aria-label="Close"
            class="absolute top-3 right-3 grid place-items-center w-9 h-9 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            ✕
          </button>

          <p class="text-3xl">🎉</p>
          <h2 id="tone-offer-title" class="mt-2 text-xl font-bold text-gray-900 pr-8">
            All {{ items.length }} tones down. Now point it at any word you want.
          </h2>
          <p class="mt-2 text-sm text-gray-600 leading-relaxed">
            A free account scores <span class="font-semibold text-gray-800">15 recordings a day</span>
            on any of 300+ course words and their example sentences — same pitch curve, real
            vocabulary. No card.
          </p>

          <a
            :href="tonePracticeSignupUrl"
            @click="track('tone_trainer_signup_clicked', { placement: 'modal', dialect: dialect })"
            class="mt-5 w-full inline-flex items-center justify-center font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-6 py-3.5 rounded-xl shadow-sm transition-colors"
          >
            Unlock any word — free →
          </a>
          <a
            :href="demoUrl"
            @click="track('tone_trainer_cta_clicked', { placement: 'modal' })"
            class="mt-2 w-full inline-flex items-center justify-center font-semibold bg-white border border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-800 px-6 py-3.5 rounded-xl transition-colors"
          >
            Try a 60-second lesson →
          </a>
          <button
            @click="dismissOffer('keep_practising')"
            class="mt-3 w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Keep practising tones
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { apiGet } from '../api/client'
import { track } from '../utils/analytics'
import { newAudioRecorder } from '../utils/recording'
import { scoreShape } from '../utils/tone_shape'
import ToneContour from './shared/ToneContour.vue'

const props = defineProps({
  itemsUrl:        { type: String, required: true },
  contourUrl:      { type: String, required: true },
  referenceUrl:    { type: String, required: true },
  demoUrl:         { type: String, required: true },
  backUrl:         { type: String, required: true },
  registrationUrl: { type: String, default: '/registration/new' },
  // Signup that lands on the members' tone trainer instead of the dashboard.
  // The whole pitch here is "this same tool, on any word" — dropping someone on
  // the course overview after that promise loses the thread.
  tonePracticeSignupUrl: { type: String, default: '/registration/new?next=%2Ftone-practice' },
})

const loading = ref(true)
const items = ref([])
const current = ref(null)
const currentKind = ref('syllable')
const recState = ref('idle') // idle, recording, processing
const userContour = ref(null)
const referenceContour = ref(null)
const errorMessage = ref('')
const retrySeconds = ref(0)

// Dialect toggle: south (Saigon, default) / north (Hanoi) when the backend
// advertises both. `accent` names the reference in the copy.
const dialect = ref('south')
const accent = ref('Saigon')
const dialects = ref([])

// Conversion moment: once someone records, we show feedback + a handoff to the
// members' tool. `recordedIds` drives the "X of N tones tried" progress — kept
// per dialect, since the North and South tone sets are different exercises.
//
// Persisted, because completing the set is the thing the handoff is gated on
// and most visitors don't finish it in one sitting: they record a couple of
// tones, leave, and come back. An in-memory counter reset them to zero on every
// return, which is why the completion CTA had never once been clicked.
const PROGRESS_KEY = 'tone_trainer_recorded_v1'

function loadProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    if (!parsed || typeof parsed !== 'object') return {}
    // Trust nothing from storage: keep only string ids under string keys.
    return Object.fromEntries(
      Object.entries(parsed)
        .filter(([ , ids ]) => Array.isArray(ids))
        .map(([ d, ids ]) => [ d, ids.filter(id => typeof id === 'string') ])
    )
  } catch (_e) {
    return {} // private-mode Safari throws on access — progress is a nicety, never a blocker
  }
}

function saveProgress() {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(recordedByDialect.value))
  } catch (_e) { /* storage full or blocked — the in-memory copy still works this session */ }
}

const recordedByDialect = ref(loadProgress())
const recordedIds = computed(() => recordedByDialect.value[dialect.value] || [])
const feedback = ref(null)
const hasRecorded = computed(() => Object.values(recordedByDialect.value).some(ids => ids.length > 0))
const allTonesTried = computed(() => items.value.length > 0 && recordedIds.value.length >= items.value.length)

// Instagram/Facebook in-app browsers are the single biggest mic failure source
// here (Instagram is our top referrer, and mic_denied is our #2 error): their
// webviews either deny getUserMedia outright or silently capture nothing. We
// can't fix their webview, so we tell people how to escape it.
const inAppBrowser = ref(false)

function detectInAppBrowser() {
  const ua = navigator.userAgent || ''
  // Instagram/FB webviews; Android FB uses FB_IAB. Deliberately narrow — a false
  // positive nags a user whose mic works fine.
  return /Instagram|FBAN|FBAV|FB_IAB/i.test(ua)
}

const inAppEscapeHint = computed(() =>
  /Android/i.test(navigator.userAgent || '')
    ? 'Tap the ⋮ menu at the top right, then "Open in Chrome".'
    : 'Tap the ••• menu at the top right, then "Open in browser".'
)

// True once the browser has actually handed us a stream — drives the priming
// line (shown only until the permission is granted).
const micGranted = ref(false)

// After a hard denial the browser stops prompting, so recovery means pointing at
// the specific control that resets it. Wording differs per browser and getting
// it wrong is worse than saying nothing useful, so this covers the three that
// account for essentially all of our denials (Mobile Safari, Mobile Chrome,
// Desktop Chrome) and falls back to something generic.
const micResetHint = computed(() => {
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua)) {
    return 'Mic access is blocked. Tap "aA" in the address bar → Website Settings → Microphone → Allow, then try again.'
  }
  if (/Android/i.test(ua)) {
    return 'Mic access is blocked. Tap the 🔒 in the address bar → Permissions → Microphone → Allow, then reload.'
  }
  return 'Mic access is blocked. Click the 🔒 (or camera icon) in your address bar, allow the microphone, then reload.'
})

const nextUntried = computed(() =>
  items.value.find(i => !recordedIds.value.includes(i.id)) || null
)

// The offer used to live only at the bottom of the page, below the chart and the
// locked card — past the fold on every screen we checked, on a tool where people
// record and leave. ~66 sessions/30d reached the completion state and none of
// them clicked, which is at least as likely to be "never scrolled to it" as
// "read it and declined". So the earned moment now comes to the visitor.
//
// Deliberately only at set completion: a modal on every recording would be
// nagware on a tool whose whole appeal is that it's free and unobtrusive.
// The modal and the bar are dismissed SEPARATELY and on purpose. Sharing one flag
// (the first cut) meant closing the modal also killed the bar for 30 days — and
// since anyone with a completed set gets the modal first, the bar could
// essentially never appear for the people it was built for. Refusing a modal that
// covers the screen is not the same as refusing a one-line bar that doesn't, so
// they get their own answers.
// Modal OFF as of 2026-07-21, code intentionally kept intact. The sticky bar
// covers everyone who records; the modal only ever fired for the ~31% who finish
// the whole tone set, so it bought a little extra pressure on a third of the
// audience at the cost of being a popup on the page people share. Flip to true to
// re-enable — but do it as a deliberate test AFTER the bar has a baseline,
// otherwise the two surfaces are indistinguishable in the funnel.
const OFFER_MODAL_ENABLED = false

const MODAL_DISMISSED_KEY = 'tone_trainer_offer_dismissed_v1'
const STICKY_DISMISSED_KEY = 'tone_trainer_sticky_dismissed_v1'
// Dismissal expires rather than being permanent: the people who come back are the
// most engaged visitors we have (only ~6% return at all), and silencing the offer
// on them forever is the wrong trade. A month is long enough that nobody feels
// nagged and short enough that a returning regular gets asked again.
const DISMISSED_DAYS = 30
const showOfferModal = ref(false)
// Bumped on every dismissal so the computed below re-reads localStorage — reading
// storage inside a computed is otherwise invisible to reactivity and the bar
// would not reappear (or disappear) until some other dependency changed.
const dismissalTick = ref(0)

function dismissedWithinWindow(key) {
  try {
    const at = Number(localStorage.getItem(key))
    if (!at) return false
    return (Date.now() - at) < DISMISSED_DAYS * 24 * 60 * 60 * 1000
  } catch (_e) {
    return false
  }
}

function recordDismissal(key) {
  try { localStorage.setItem(key, String(Date.now())) } catch (_e) { /* non-essential */ }
  dismissalTick.value++
}

function dismissOffer(reason) {
  showOfferModal.value = false
  recordDismissal(MODAL_DISMISSED_KEY)
  track('tone_trainer_offer_dismissed', { reason, surface: 'modal' })
}

function dismissStickyOffer() {
  recordDismissal(STICKY_DISMISSED_KEY)
  track('tone_trainer_offer_dismissed', { reason: 'close_button', surface: 'sticky_bar' })
}

// Persistent low-key ask, pinned to the viewport once the visitor has recorded.
// Every offer on this page used to sit below the fold on a tool people use and
// leave without scrolling. This one follows them. It stays deliberately quieter
// than the modal — one line, no lock-first framing above the tool, and it only
// appears AFTER the first recording, so nobody is asked before they've got
// something. Hidden while the modal is up so there's only ever one live ask.
const showStickyOffer = computed(() => {
  dismissalTick.value // eslint-disable-line no-unused-expressions -- reactivity hook for the localStorage read below
  return hasRecorded.value && !showOfferModal.value && !dismissedWithinWindow(STICKY_DISMISSED_KEY)
})

// P3 instrumentation. `tone_trainer_set_completed` fires on the transition, so it
// undercounts impressions: a returner arrives with the set already complete (or
// switches dialects back) and sees the offer without ever crossing the boundary.
// Without a real denominator a 0 in the funnel is unreadable — is the offer
// never seen, or seen and refused? Fires at most once per page load, tagged with
// the placement that actually rendered.
let offerShownTracked = false

watch(allTonesTried, (complete) => {
  if (!complete) return
  if (OFFER_MODAL_ENABLED && !dismissedWithinWindow(MODAL_DISMISSED_KEY)) showOfferModal.value = true
  if (offerShownTracked) return
  offerShownTracked = true
  track('tone_trainer_offer_shown', {
    placement: showOfferModal.value ? 'modal' : 'inline',
    dialect: dialect.value,
    tones: items.value.length,
  })
}, { immediate: true })

// The bar is the live surface now that the modal is off, so it needs its own
// impression or its CTR has no denominator. Tracked once per page load, on the
// first time it actually renders — not when it merely becomes eligible.
let stickyShownTracked = false

watch(showStickyOffer, (visible) => {
  if (!visible || stickyShownTracked) return
  stickyShownTracked = true
  track('tone_trainer_offer_shown', {
    placement: 'sticky_bar', dialect: dialect.value, tones: items.value.length,
  })
}, { immediate: true })

// Esc closes, and the page behind must not scroll while it's open (iOS in
// particular will happily scroll the body under a fixed overlay).
function onOfferKeydown(e) {
  if (e.key === 'Escape' && showOfferModal.value) dismissOffer('escape')
}

watch(showOfferModal, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

onMounted(() => document.addEventListener('keydown', onOfferKeydown))
onUnmounted(() => {
  document.removeEventListener('keydown', onOfferKeydown)
  document.body.style.overflow = ''
})

// The tone card sits above this button, so advancing without scrolling would
// leave the learner staring at an unchanged screen.
const toneCard = ref(null)

function goToNextUntried() {
  if (!nextUntried.value) return
  const item = nextUntried.value
  selectItem(item)
  track('tone_trainer_next_tone_clicked', { item: item.id, dialect: dialect.value })
  toneCard.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}


const target = computed(() => current.value?.[currentKind.value] || { text: '', meaning: '', audio: null })

// Reference contours are static — fetch once per dialect/item/kind and remember.
const referenceCache = {}

async function loadItems() {
  try {
    const data = await apiGet(`${props.itemsUrl}?dialect=${dialect.value}`)
    items.value = data.items || []
    accent.value = data.accent || 'Saigon'
    dialects.value = data.dialects || []
    if (items.value.length) {
      current.value = items.value[0]
      currentKind.value = 'syllable'
      resetAttempt()
      loadReference()
    }
  } catch (_e) {
    items.value = []
  }
}

function selectDialect(id) {
  if (dialect.value === id) return
  dialect.value = id
  loadItems()
  track('tone_trainer_dialect_selected', { dialect: id })
}

function selectItem(item) {
  current.value = item
  currentKind.value = 'syllable'
  resetAttempt()
  loadReference()
  track('tone_trainer_tone_selected', { item: item.id, dialect: dialect.value })
}

function selectKind(kind) {
  currentKind.value = kind
  resetAttempt()
  loadReference()
  track('tone_trainer_kind_selected', { item: current.value.id, kind })
}

function resetAttempt() {
  userContour.value = null
  referenceContour.value = null
  errorMessage.value = ''
  feedback.value = null
}

function playReference() {
  if (!target.value.audio) return
  try {
    new Audio(target.value.audio).play().catch(() => {})
    track('tone_trainer_item_played', { item: current.value.id, kind: currentKind.value })
  } catch (_e) { /* audio must never break the page */ }
}

async function loadReference() {
  const key = `${dialect.value}/${current.value.id}/${currentKind.value}`
  if (referenceCache[key]) { referenceContour.value = referenceCache[key]; return }
  try {
    const data = await apiGet(`${props.referenceUrl}?item=${current.value.id}&kind=${currentKind.value}&dialect=${dialect.value}`)
    referenceCache[key] = data?.points || null
    referenceContour.value = referenceCache[key]
  } catch (_e) {
    referenceContour.value = null
  }
}

let mediaRecorder = null
let audioChunks = []
let autoStopTimer = null

// Tones are sub-second utterances; auto-stop keeps recordings small and the
// public analysis endpoint cheap even if someone walks away mid-recording.
const MAX_RECORDING_MS = 6000

function stopRecording() {
  clearTimeout(autoStopTimer)
  if (mediaRecorder?.state === 'recording') mediaRecorder.stop()
  recState.value = 'processing'
}

async function toggleRecording() {
  if (recState.value === 'recording') {
    stopRecording()
    return
  }
  errorMessage.value = ''
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    audioChunks = []
    mediaRecorder = newAudioRecorder(stream) // picks webm or mp4 per browser (iOS Safari has no webm)
    mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.push(e.data) }
    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop())
      await analyze(new Blob(audioChunks, { type: mediaRecorder.mimeType || 'audio/webm' }))
    }
    mediaRecorder.start()
    micGranted.value = true
    recState.value = 'recording'
    autoStopTimer = setTimeout(stopRecording, MAX_RECORDING_MS)
  } catch (e) {
    // getUserMedia failures are not interchangeable and the old catch-all
    // ("allow it and try again") was unactionable for most of them: once a
    // browser has recorded a denial it won't re-prompt, so "try again" does
    // nothing until the user changes a site setting. Branch on the DOMException
    // name and give each case its real next step. error_name also rides along to
    // PostHog so the mic_denied bucket stops being a single opaque number.
    const name = e?.name || 'UnknownError'
    if (inAppBrowser.value) {
      // A webview may not be able to grant the permission at all — escape first.
      errorMessage.value = `This in-app browser blocks the mic. ${inAppEscapeHint.value}`
    } else if (name === 'NotAllowedError' || name === 'SecurityError') {
      errorMessage.value = micResetHint.value
    } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      errorMessage.value = "No microphone found — plug one in or switch devices, then try again."
    } else if (name === 'NotReadableError' || name === 'AbortError') {
      errorMessage.value = "Your mic is busy in another app or tab. Close it and try again."
    } else {
      errorMessage.value = 'Microphone access is needed — allow it and try again.'
    }
    track('tone_trainer_error', {
      reason: 'mic_denied', in_app_browser: inAppBrowser.value, error_name: name,
    })
  }
}

// While counting down the mic button is disabled — retrying early would just
// 429 again and restart the clock. The interval owns errorMessage so the
// countdown survives resetAttempt() (the limit is per-visitor, not per-tone).
let retryTimer = null

function startRetryCountdown(seconds) {
  clearInterval(retryTimer)
  retrySeconds.value = seconds
  errorMessage.value = `Too many recordings — try again in ${seconds}s.`
  retryTimer = setInterval(() => {
    retrySeconds.value -= 1
    if (retrySeconds.value <= 0) {
      clearInterval(retryTimer)
      errorMessage.value = ''
    } else {
      errorMessage.value = `Too many recordings — try again in ${retrySeconds.value}s.`
    }
  }, 1000)
}

onUnmounted(() => clearInterval(retryTimer))

async function analyze(blob) {
  try {
    const formData = new FormData()
    formData.append('audio', blob, blob.type.includes('mp4') ? 'recording.m4a' : 'recording.webm')
    const res = await fetch(props.contourUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData,
    })
    if (!res.ok) {
      // 422 = the recording itself was rejected as undecodable (usually a
      // too-short or truncated capture) — recording again fixes it.
      if (res.status === 429) {
        startRetryCountdown(Number(res.headers.get('Retry-After')) || 60)
        track('tone_trainer_error', { reason: 'rate_limited' })
      } else if (res.status === 422) {
        errorMessage.value = "That recording didn't come through — hold the button a beat longer and try again."
        track('tone_trainer_error', { reason: 'bad_recording' })
      } else {
        errorMessage.value = 'Pitch analysis is unavailable right now. Try again shortly.'
        track('tone_trainer_error', { reason: 'unavailable' })
      }
      return
    }
    const data = await res.json()
    if (!data.points || data.points.length < 2) {
      errorMessage.value = "We couldn't hear a voice in that recording — get closer to the mic and try again."
      track('tone_trainer_error', { reason: 'no_voice' })
      return
    }
    userContour.value = data.points
    feedback.value = scoreShape(data.points, referenceContour.value, accent.value)
    const recorded = recordedByDialect.value[dialect.value] ||= []
    if (!recorded.includes(current.value.id)) {
      recorded.push(current.value.id)
      saveProgress()
      if (allTonesTried.value) track('tone_trainer_set_completed', { dialect: dialect.value, tones: items.value.length })
    }
    track('tone_trainer_recorded', { item: current.value.id, kind: currentKind.value, dialect: dialect.value })
  } catch (_e) {
    errorMessage.value = 'Pitch analysis is unavailable right now. Try again shortly.'
    track('tone_trainer_error', { reason: 'unavailable' })
  } finally {
    recState.value = 'idle'
  }
}

onMounted(async () => {
  inAppBrowser.value = detectInAppBrowser()
  track('tone_trainer_started', { in_app_browser: inAppBrowser.value })
  await loadItems()
  loading.value = false
})
</script>
